import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api, { setAuthToken } from '../api/api';

const AuthContext = createContext(null);

// --- SSO TOKEN CAPTURE ENGINE ---
const captureSSOToken = () => {
  try {
    const url = new URL(window.location.href);
    // console.log('[Booking SSO] Checking URL for token:', window.location.href);
    
    // 1. Check Search Params
    let token = url.searchParams.get('sso_token');
    // if (token) console.log('[Booking SSO] Found token in searchParams:', token.substring(0, 10) + '...');
    
    // 2. Check Hash
    if (!token && window.location.hash) {
      // console.log('[Booking SSO] Checking hash:', window.location.hash);
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : hash);
      token = params.get('sso_token');
      // if (token) console.log('[Booking SSO] Found token in hash:', token.substring(0, 10) + '...');
    }

    if (token) {
      // console.log('[Booking SSO] Token captured successfully, saving to localStorage');
      localStorage.setItem('token', token);
      return token;
    }
  } catch (e) {
    // console.error('[Booking SSO] CAPTURE ERROR:', e);
  }
  
  const storedToken = localStorage.getItem('token');
  // if (storedToken) console.log('[Booking SSO] Using stored token from localStorage');
  return storedToken;
};

const globalInitialToken = captureSSOToken();

export function AuthProvider({ children }) {
  const [token, setToken] = useState(globalInitialToken);
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // console.log('[Booking Auth] Effect triggered, token exists:', !!token);
    setAuthToken(token);
    if (!token) {
      // One last check just in case it appeared late
      const lateToken = captureSSOToken();
      if (lateToken) {
        // console.log('[Booking Auth] Token found late, updating state');
        setToken(lateToken);
        return;
      }
      // console.log('[Booking Auth] No token found, setting loading to false');
      setLoading(false);
      return;
    }

    setLoading(true);
    // console.log('[Booking Auth] Verifying token with /auth/profile...');
    (async () => {
      try {
        // If the API call fails with 404, it might be because the baseURL in api.js 
        // is not correctly pointing to the backend.
        const { data } = await api.get('/auth/profile');
        // console.log('[Booking Auth] Verification SUCCESS:', data);
        const userData = data.user || data;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Clean URL after successful verification
        if (window.location.href.includes('sso_token')) {
          // console.log('[Booking Auth] Cleaning SSO token from URL');
          setTimeout(() => {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }, 1000);
        }
      } catch (error) {
        // console.error('[Booking Auth] Verification FAILED:', error.response?.data || error.message);
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      isAuthenticated: !!user && !!token,
      async login(payload) {
        const { data } = await api.post('/auth/login', payload);
        const { token: newToken, user: userData } = data;
        setToken(newToken);
        setUser(userData);
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
        setAuthToken(newToken);
      },
      async register(payload) {
        const { data } = await api.post('/auth/register', payload);
        const { token: newToken, user: userData } = data;
        setToken(newToken);
        setUser(userData);
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
        setAuthToken(newToken);
      },
      async logout() {
        await api.post('/auth/logout').catch(() => undefined);
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setAuthToken(null);
        window.location.href = '/login';
      },
    }),
    [token, user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
